import { useEffect, useState } from 'react';
import { Field as FormikField, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { DollarSign } from '@styled-icons/fa-solid/DollarSign';

import { RootState } from 'store';
import { setLeadData } from 'leadData/leadSlice';
import {
  ContractFlowClient,
  Offer,
  Term,
} from 'v3/api/client/ContractFlowClient';
import { handleErrorMessage } from 'v3/utils/errorHandler';
import { parseCurrency } from 'v3/utils/parse';
import {
  DEAL_VALUE_RANGE_MESSAGE,
  isDealValueInRange,
} from 'v3/utils/dealValue';
import { LOAN_TERM_MONTHS, monthsToWeeks } from 'v3/utils/loanTerm';
import { useQuery } from 'v3/hooks/useQuery';
import { useMixpanel } from 'v3/hooks/useMixpanel';
import SidebarBox from 'v3/components/SidebarBox';
import Field from 'v3/components/Field';
import Checkbox from 'v3/components/Checkbox';
import Steps from 'v3/components/Steps';
import Button from 'v3/components/Button';
import OfferBox from 'v3/components/OfferBox';
import Collapse from 'v3/components/Collapse';
import LoadingSpinner from 'v3/components/LoadingSpinner';
import ErrorFocus from 'v3/components/ErrorFocus';
import LoadingState from 'v3/components/LoadingState';
import FlowScreen from 'v3/components/FlowScreen';
import ReferralField from './ReferralField';
import VideoPanel from 'v3/components/VideoPanel';
import videoPosterImage from 'v3/assets/images/video-background.png';

import * as S from './styles';
import { UsdFormatter } from 'v3/utils/formatNumber';

const PAYMENT_CYCLES = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'weekly', label: 'Weekly' },
] as const;

/* Down payment is no longer adjustable here, so its rules (and the cross-field
   comparison against the vehicle price) go with it. */
const recalculateSchema = Yup.object().shape({
  vehiclePrice: Yup.string().test(
    'dealValue',
    DEAL_VALUE_RANGE_MESSAGE,
    (value) => (value ? isDealValueInRange(parseCurrency(value)) : false)
  ),
});



const reviewOfferSchema = Yup.object().shape({
  agreeTerm: Yup.boolean()
    .required(
      "Please, confirm that you understand how Savings Club works and when you'll get your car."
    )
    .oneOf(
      [true],
      "Please, confirm that you understand how Savings Club works and when you'll get your car."
    ),
});

const ReviewOffer = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const testFlag = query.get('test') ?? '';
  const { leadData } = useSelector((state: RootState) => state.lead);
  const mp = useMixpanel();

  const [offer, setOffer] = useState({} as Offer);
  const [range, setRange] = useState<[number, number]>([1, 1]);
  const [isOfferLoading, setIsOfferLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [halfPayment, setHalfPayment] = useState(0);
  const [weeklyHalfPayment, setWeeklyHalfPayment] = useState(0);
  const [showChangeOffer, setShowChangeOffer] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState<'weekly' | 'monthly'>(
    leadData.weeklyCycle ? 'weekly' : 'monthly'
  );

  const initialValues = {
    vehiclePrice: leadData.dealValue,
    term: String(leadData.term) ?? '84',
  };

  const isWeeklyCycle = selectedCycle === 'weekly';

  // useEffect(() => {
  //   const getDealChances = async () => {
  //     const { data } = await ContractFlowClient().getDealChances({
  //       vehiclePrice: leadData.dealValue,
  //       term: leadData.term,
  //       creditScore: leadData.creditScore,
  //       downPayment: leadData.contractFlowDownPayment,
  //       halfPaymentSelected: leadData.contractFlowHalfPaymentSelected,
  //     });

  //     if (data) {
  //       setRange(data.userChanceRange);
  //     }
  //   };

  //   getDealChances();
  // }, [
  //   leadData.dealValue,
  //   leadData.term,
  //   leadData.creditScore,
  //   leadData.contractFlowDownPayment,
  //   leadData.contractFlowHalfPaymentSelected,
  // ]);

  useEffect(() => {
    const calculateFees = async (term: string) => {
      setIsOfferLoading(true);

      const { data } = await ContractFlowClient().calculateFee(
        leadData.contractFlowToken,
        Number(leadData.term),
        leadData.contractFlowDownPayment
      );

      if (data) {
        const { monthlyPayment, weeklyPayment, managementFee } = data;

        if (leadData.contractFlowHalfPaymentSelected) {
          const [{ data: monthlyHalfData }, { data: weeklyHalfData }] =
            await Promise.all([
              ContractFlowClient().getHalfPayment(managementFee, monthlyPayment),
              ContractFlowClient().getHalfPayment(managementFee, weeklyPayment),
            ]);

          if (monthlyHalfData) {
            setHalfPayment(monthlyHalfData.data.halfPayment);
          }
          if (weeklyHalfData) {
            setWeeklyHalfPayment(weeklyHalfData.data.halfPayment);
          }
        }
        // const paymentAmount = leadData.weeklyCycle ? weeklyPayment : monthlyPayment;
        setOffer({ ...data });
      }

      setIsOfferLoading(false);
    };

    calculateFees(leadData.term);
  }, [leadData]);

  useEffect(() => {
    mp.fireAccessReviewOffer();
  }, [mp]);

  useEffect(() => {
    setShowChangeOffer(!leadData.founderInvite);
  }, [leadData.founderInvite]);

  return (
    <FlowScreen>
      <S.InfoPane>
        {/* Eyebrow outside the card, title inside. The numbering is orientation
            about the flow, not content of the card. */}
        <S.Eyebrow>
          <span className="phase">Onboarding</span>
          <span className="rest"> · Step 2 of 3</span>
        </S.Eyebrow>
        <S.CardShell>
        <SidebarBox variant="secondary" as="section">
          <S.InfoTitle>What you&apos;d pay</S.InfoTitle>
          {(isOfferLoading && !leadData) ? (
            <>
              <S.LoadingWrapper>
                <LoadingState>
                  <S.LoadingContent>
                    Just a moment.
                    <br />
                    We're fetching your offers for you...
                  </S.LoadingContent>
                </LoadingState>
              </S.LoadingWrapper>
            </>
          ) : (
            <>
              {/* Frequency is the only control before the summary: it changes the
                  headline, so the choice precedes the number. The referral comes
                  after, next to the discount it produces. */}
              <S.Adjust>
                <S.CycleFieldset>
                  <S.CycleLegend>Payment frequency</S.CycleLegend>
                  <S.CycleWrapper>
                    {PAYMENT_CYCLES.map(({ id, label }) => (
                      <S.Chip key={id} $selected={selectedCycle === id}>
                        <input
                          type="radio"
                          name="paymentCycle"
                          value={id}
                          checked={selectedCycle === id}
                          onChange={() => setSelectedCycle(id)}
                        />
                        {label}
                      </S.Chip>
                    ))}
                  </S.CycleWrapper>
                </S.CycleFieldset>
              </S.Adjust>

              <S.OfferBox>
                <OfferBox
                  loading={isOfferLoading}
                  weeklyCycle={selectedCycle === 'weekly'}
                  monthlyPayment={offer.monthlyPayment}
                  weeklyPayment={offer.weeklyPayment}
                  term={Number(leadData.term)}
                  weekTerm={Number(offer.weekTerm)}
                  halfPayment={selectedCycle === 'weekly' ? 0 : halfPayment}
                  weeklyHalfPayment={selectedCycle === 'weekly' ? weeklyHalfPayment : undefined}
                  amount={leadData.dealValue}
                  eapr={offer.apy}
                  paymentOption={
                    leadData.contractFlowHalfPaymentSelected ? 'half' : 'full'
                  }
                  downPayment={leadData.contractFlowDownPayment}
                  range={range}
                  referralCode={leadData.referralCode}
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  referralCodeSavings={offer.referralCodeSavings}
                  firstPayment={selectedCycle === 'weekly' ? offer.firstWeeklyPayment : undefined}
                  statsLayout="grid"
                  showDownPayment={false}
                >
                  <S.AfterOffer>
                    <ReferralField
                      clubId={leadData.clubId}
                      cfUuid={leadData.contractFlowToken}
                      initialCode={leadData.referralCode}
                      initialDescription={leadData.referralCodeShortDescription}
                      onApplied={(code, contractFlow) => {
                        // A referral carries its own terms — fee, half payment and
                        // payment cycle. The cycle chip is local state, so resync it
                        // from the response or it would keep showing the previous
                        // referral's cycle while the offer below used the new one.
                        if (contractFlow) {
                          setSelectedCycle(
                            contractFlow.weeklyCycle ? 'weekly' : 'monthly'
                          );
                        }

                        dispatch(
                          setLeadData({
                            // applyReferral returns the already-recalculated flow;
                            // using the response makes the summary reflect the real
                            // discount instead of just stamping the code on screen.
                            leadData: contractFlow
                              ? { ...leadData, ...contractFlow }
                              : { ...leadData, referralCode: code },
                          })
                        );
                      }}
                    />
                  </S.AfterOffer>

                  {showChangeOffer && (
                    <S.CollapseContainer>
                      <Collapse
                        title="Want to make a change?"
                        variant="secondary"
                      >
                        <Formik
                          initialValues={initialValues}
                          /* Recalculating rewrites leadData, so without this the
                             form keeps the values it mounted with — the summary
                             would read 104 weeks while the term still showed 416. */
                          enableReinitialize
                          validationSchema={recalculateSchema}
                          onSubmit={async (recalculateData) => {
                            const { data, error } =
                              await ContractFlowClient().updateReviewOffer({
                                advanceStep: false,
                                cfUuid: leadData.contractFlowToken,
                                dealValue: parseCurrency(
                                  String(recalculateData.vehiclePrice)
                                ),
                                months: Number(recalculateData.term),
                                testFlag,
                              });

                            if (data) {
                              mp.fireRecalculateOffer({
                                newValue: data.dealValue,
                                months: data.term,
                                downPayment: data.contractFlowDownPayment,
                                paymentOption:
                                  data.contractFlowHalfPaymentSelected
                                    ? 'half'
                                    : 'full',
                              });

                              dispatch(
                                setLeadData({
                                  leadData: data,
                                })
                              );
                            }

                            if (error) {
                              handleErrorMessage(error);
                            }
                          }}
                        >
                          {({ errors, touched, isValid, values }) => (
                            <Form>
                              {/* No payment-method control: half payment is granted by
                                  the referral code and nothing else, so there is
                                  nothing here for the member to choose. The offer
                                  summary above still shows it when a referral grants
                                  it. */}
                              <S.FieldsWrapper marginTop={20}>
                                <div>
                                  <Field
                                    name="vehiclePrice"
                                    label="Vehicle Price"
                                    prefix={
                                      <DollarSign width={18} height={18} />
                                    }
                                    currency
                                    feedbackMessage={errors.vehiclePrice}
                                    isValid={
                                      !(
                                        touched.vehiclePrice &&
                                        errors.vehiclePrice
                                      )
                                    }
                                  />
                                </div>
                              </S.FieldsWrapper>
                              {/* The value stays in months whatever the cycle —
                                  that is what the API takes. Only the label
                                  changes, so a member paying weekly reads the term
                                  in the unit they actually pay in. */}
                              <S.RadioLabel>
                                Loan Term ({isWeeklyCycle ? 'Weeks' : 'Months'})
                              </S.RadioLabel>
                              <S.TermWrapper>
                                {LOAN_TERM_MONTHS.map((months) => (
                                  <S.Chip
                                    key={months}
                                    $selected={values.term === String(months)}
                                  >
                                    <FormikField
                                      type="radio"
                                      name="term"
                                      value={String(months)}
                                    />
                                    {isWeeklyCycle
                                      ? monthsToWeeks(months)
                                      : months}
                                  </S.Chip>
                                ))}
                              </S.TermWrapper>
                              <S.ButtonWrapper>
                                <S.RecalculateButton
                                  disabled={!isValid}
                                  variant="secondary"
                                  type="submit"
                                >
                                  Recalculate
                                </S.RecalculateButton>
                              </S.ButtonWrapper>
                              <ErrorFocus />
                            </Form>
                          )}
                        </Formik>
                      </Collapse>
                    </S.CollapseContainer>
                  )}
                </OfferBox>
              </S.OfferBox>

              {/* The video is a section of the card, not a card of its own: loose
                  underneath at 320px against the card's ~590px it read as orphaned.
                  It lives here because the consent just below asserts understanding
                  of how Savings Club works — and this video is what explains it. */}
              <S.VideoSlot>
                <VideoPanel
                  flat
                  orientation="row"
                  title="When do I get my car?"
                  caption="Your turn can happen at any time between the first and last month of your term."
                  videoLink="https://player.vimeo.com/video/1054655264?h=d2b02730e1&badge=0&autopause=0&player_id=0&app_id=58479"
                  posterImage={videoPosterImage}
                />
              </S.VideoSlot>

              <S.Footer>
              <Formik
                isInitialValid={false}
                initialValues={{
                  agreeTerm: false,
                }}
                validationSchema={reviewOfferSchema}
                onSubmit={async () => {
                  setIsLoading(true);

                  const { data, error } =
                    await ContractFlowClient().updateReviewOffer({
                      months: Number(leadData.term),
                      cfUuid: leadData.contractFlowToken,
                      weeklyCycle: selectedCycle === 'weekly',
                      testFlag,
                    });

                  if (data) {
                    mp.registerOffer({
                      contractDuration: data.term,
                      totalCost: offer.groupValueWithFee,
                      monthlyInstallment: offer.monthlyPayment,
                      halfPaymentSelected: data.contractFlowHalfPaymentSelected,
                      downPayment: data.contractFlowDownPayment,
                    });

                    dispatch(
                      setLeadData({
                        leadData: {
                          ...data,
                          referralCodeSavings: offer.referralCodeSavings,
                          weeklyCycle: selectedCycle === 'weekly',
                        },
                        currentStep: data.contractFlowStep,
                      })
                    );
                  }

                  if (error) {
                    setIsLoading(false);
                    handleErrorMessage(error);
                  }
                }}
              >
                {({ errors, touched }) => (
                  /* id so the CTA can live outside the card and still submit this
                     form (HTML's `form` attribute), as on Getting Started. */
                  <Form id="review-offer-form">
                    <Checkbox
                      required
                      name="agreeTerm"
                      isValid={touched.agreeTerm && !errors.agreeTerm}
                      feedbackMessage={errors.agreeTerm}
                    >
                      <>
                        {/* No "By clicking the button": the screen requires the tick,
                            and saying the consent is the click contradicts the UI —
                            the same correction made to the FCRA notice. */}
                        I understand how Savings Club works.
                      </>
                    </Checkbox>

                    <ErrorFocus />
                  </Form>
                )}
              </Formik>
              </S.Footer>
            </>
          )}
        </SidebarBox>
        </S.CardShell>

        {/* CTA outside the card, right-aligned — same pattern as Getting Started.
            The `form` attribute keeps the submit wired to the <Form> that stays
            inside the card, without touching Formik or onSubmit. */}
        {!(isOfferLoading && !leadData) && (
          <S.ButtonSection>
            <Button
              variant="brandPrimary"
              disabled={isLoading}
              type="submit"
              form="review-offer-form"
            >
              {/* The spinner defaults to white, which is invisible now the CTA is. */}
              {isLoading ? (
                <LoadingSpinner color="brandPrimary100" />
              ) : (
                'Review my contract'
              )}
            </Button>
          </S.ButtonSection>
        )}
      </S.InfoPane>
    </FlowScreen>
  );
};

export default ReviewOffer;
